/* eslint-disable no-restricted-properties */
import { useClient, useQuery } from '@weenat/client'
import { canUser, ops } from '@weenat/client/dist/core/access'
import {
  isNetworkMember,
  useIsMemberOfCommunityNetwork,
  useIsPartOfANetwork
} from '@weenat/client/dist/core/orgs'
import { InvitationStatus } from '@weenat/client/dist/enums'
import { RequestType } from '@weenat/client/dist/resources/types'
import { useIntl } from '@weenat/wintl'
import { useOrgContext } from 'app/orgProvider'
import { useMatch } from 'app/routx-router'
import useDisclosure from 'app/src/hooks/useDisclosure'
import useToasts from 'app/src/hooks/useToasts'
import Button from 'app/src/kit/Button'
import { IconProps } from 'app/src/kit/Icon'
import Icons from 'app/src/kit/Icons'
import Link from 'app/src/kit/LinkComponent'
import Modal from 'app/src/kit/Modal'
import QRCode from 'app/src/kit/QRCode'
import SeparatorBox from 'app/src/kit/SeparatorBox'
import SidePanelLink from 'app/src/kit/SidePanelLink'
import SidePanelLinkGroup from 'app/src/kit/SidePanelLinkGroup'
import Text from 'app/src/kit/Text'
import { LogoSphere } from 'app/src/kit/icons/logos'
import Panel from 'app/src/kit/panels/Panel'
import PanelLinkSection from 'app/src/kit/panels/PanelLinkSection'
import PanelSection from 'app/src/kit/panels/PanelSection'
import TextTooltip from 'app/src/kit/tooltips/TextTooltip'
import { useLoginAs, useToken } from 'app/state'
import { useUserContext } from 'app/userProvider'
import copy from 'copy-to-clipboard'
import { isNil, noop } from 'lodash-es'
import { styled } from 'styled-components'
import FavoritesNavSection from 'app/src/favorites/FavoritesNavSection'
import RedesignSelectOrg from '../DashboardMap/RedesignSelectOrg'
import AccountAvatar from './AccountAvatar'
import AccountNotifications from './AccountNotifications'
import AddModal from './AddModal'

export const NAV_PANEL_WIDTH = 256

const genericIconProps: IconProps = { $size: 'lg' }

const ScrollablePanelSection = styled(PanelSection)`
  flex: 1;

  overflow-y: hidden;
  scroll-behavior: smooth;
  scrollbar-gutter: stable both-edges;

  &:hover {
    overflow-y: scroll;
  }
`

const NavPanel = () => {
  const client = useClient()
  const { t } = useIntl()
  const org = useOrgContext()
  const { me } = useUserContext()
  const [loginAs] = useLoginAs()
  const [token] = useToken()
  const { addInfoToast } = useToasts()
  const { isMemberOfCommunityNetwork } = useIsMemberOfCommunityNetwork(org)
  const { isPartOfANetwork } = useIsPartOfANetwork(token)

  const debugModal = useDisclosure()

  const matchFarms = useMatch(`/farms/*`)
  const matchAdmin = useMatch(`/farms/*/admin/*`)

  const pathToCurrentOrganization = `/farms/${org?.id}/admin` as const
  const accessContext = !isNil(org) ? { organization: org } : null

  const isNetwork = !isNil(org?.networkType)

  // FARM ACCESS
  const canViewSentMemberInvitations = !isNil(accessContext)
    ? canUser(ops.view, 'organizationInvitations', accessContext)
    : false
  const canViewMembers = !isNil(accessContext) ? canUser(ops.view, 'members', accessContext) : false
  const canViewDevices = !isNil(accessContext) ? canUser(ops.view, 'devices', accessContext) : false
  const canViewOptions = !isNil(accessContext) ? canUser(ops.view, 'options', accessContext) : false

  // NETWORK ACCESS
  const canEditNetwork = !isNil(accessContext)
    ? canUser(ops.edit, 'networks', accessContext)
    : false
  const canViewNetworkOptionRequests = !isNil(accessContext)
    ? canUser(ops.view, 'networkOptionRequests', accessContext)
    : false
  const canViewNetworkAdmin = !isNil(accessContext)
    ? canUser(ops.view, 'organizationNetworkAdministrationScreen', accessContext) &&
      (canEditNetwork || canViewNetworkOptionRequests)
    : false

  // BILLING ACCESS
  // Only for users that have access rights inside networks, standalone orgs, or orgs that are members of a community network
  const canViewBilling =
    !isNil(accessContext) &&
    canUser(ops.view, 'billingScreen', accessContext) &&
    (isNetwork || !isNetworkMember(org) || isMemberOfCommunityNetwork)

  // COUNT QUERIES

  // This query is only to get the total number of pending invitations sent to the current user
  const pendingInvitationsQuery = useQuery(
    client.invitations.received.getMany({ limit: 1, status: InvitationStatus.pending })
  )

  const pendingInvitations = pendingInvitationsQuery.data

  const pendingRequestsCommonQueryParams = {
    limit: 1,
    networkId: org.id,
    status: InvitationStatus.pending
  }

  // Get the total number of pending membership requests for the current network
  const membershipTrialRequestsQuery = useQuery(
    client.invitations.receivedPerNetwork.getMany({
      type: [RequestType.adherentFarmRequest, RequestType.adherentFarmRequestWithTrial],
      ...pendingRequestsCommonQueryParams
    }),
    { enabled: canEditNetwork }
  )

  const membershipTrialRequests = membershipTrialRequestsQuery.data

  // Get the total number of pending option purchase requests for the current network
  const optionsRequestQuery = useQuery(
    client.invitations.receivedPerNetwork.getMany({
      type: [RequestType.adherentFarmOptionPurchaseRequest],
      ...pendingRequestsCommonQueryParams
    }),
    { enabled: canViewNetworkOptionRequests }
  )

  const optionsRequest = optionsRequestQuery.data

  const debugUrl = new URL(
    `${window.location.protocol}//${window.location.hostname}${!isNil(window.location.port) ? `:${window.location.port}` : ''}${window.location.pathname}`
  )

  const canDebug = canUser(ops.view, 'debugMode', { account: me }) || !isNil(loginAs)

  if (canDebug) {
    debugUrl.pathname = `/me/debug`
    debugUrl.searchParams.set('login_as', loginAs?.email ?? me.email)
    debugUrl.searchParams.set('redirect', window.location.pathname + window.location.search)
  }

  const debugLink = debugUrl.toString()

  return (
    <Panel width={NAV_PANEL_WIDTH} docking='left'>
      {/* HEADER SECTION */}
      <PanelSection $gap='none'>
        {/* Logo - Account Tools */}
        <Flex $alignItems='center' $justifyContent='space-between' $width={'100%'}>
          <Link to='/'>
            <LogoSphere width={32} height={32} />
          </Link>

          <Flex $alignItems='center' $justifyContent='flex-end' $gap={'md'}>
            {canDebug ? (
              <>
                <Icons.Bug $size='lg' $p={'md'} onPress={debugModal.open} />
                <Modal
                  isOpen={debugModal.isOpen}
                  close={debugModal.close}
                  width={400}
                  title={t('titles.debug')}
                >
                  <Flex
                    $flexDirection='column'
                    $justifyContent='center'
                    $alignItems='center'
                    $gap='xl'
                    $width={'100%'}
                  >
                    <Flex
                      $flexDirection='column'
                      $justifyContent='center'
                      $alignItems='center'
                      $gap='lg'
                      $width={'100%'}
                    >
                      <Text $textAlign='center'>Login on your phone by scanning the QR code</Text>
                      <QRCode value={debugLink} size={128} />
                    </Flex>

                    <SeparatorBox />

                    <Button
                      backgroundColor='black'
                      IconLeft={Icons.BugLink}
                      variant='compact'
                      onPress={() => {
                        copy(debugLink)
                        debugModal.close()
                        addInfoToast('Bug report link copied!')
                      }}
                    >
                      Copy Debug Link
                    </Button>
                  </Flex>
                </Modal>
              </>
            ) : null}

            <AccountNotifications />
            <AccountAvatar me={me} pendingInvitationCount={pendingInvitations?.count} />
          </Flex>
        </Flex>
        <RedesignSelectOrg />
      </PanelSection>

      {/* LINKS */}
      <ScrollablePanelSection>
        <div>
          {/* FAVORITES */}
          <FavoritesNavSection />

          {/* HEAD */}
          <Flex $alignItems='center' $gap={'md'} $pb={'md'}>
            <Box $flex={1}>
              <SidePanelLink
                LeftIcon={Icons.Farm}
                label={t('titles.my_farm')}
                to={`/farms/${org.id}`}
                isActive={!isNil(matchFarms) && isNil(matchAdmin)}
              />
            </Box>
            <AddModal />
          </Flex>

          {/* ADMINISTRATION */}
          <PanelLinkSection title={t('titles.admin')}>
            {canViewDevices ? (
              <SidePanelLinkGroup
                mainLink={{
                  id: `devices`,
                  to: `${pathToCurrentOrganization}/devices`,
                  label: t('models.device.filters.all_devices', { capitalize: true }),
                  LeftIcon: Icons.SingleSensorFilled,
                  iconProps: genericIconProps,
                  matchExactPath: true
                }}
                subLinks={[
                  {
                    id: `agro-weather`,
                    to: `${pathToCurrentOrganization}/devices/agro-weather`,
                    label: t('models.agro_weather_device.model.name', { capitalize: true }),
                    iconProps: genericIconProps,
                    matchExactPath: true
                  },
                  {
                    id: `irrigation`,
                    to: `${pathToCurrentOrganization}/devices/irrigation`,
                    label: t('models.irrigation_probe.model.name', { capitalize: true }),
                    iconProps: genericIconProps,
                    matchExactPath: true
                  }
                ]}
              />
            ) : null}
            {canViewMembers ? (
              <SidePanelLink
                label={t('models.member.model.name', { capitalize: true })}
                to={`${pathToCurrentOrganization}/members`}
                matchExactPath={false}
                LeftIcon={Icons.Person}
                iconProps={genericIconProps}
              />
            ) : null}
            {canViewSentMemberInvitations ? (
              <SidePanelLink
                label={t('titles.invitations_sent_to_users')}
                to={`${pathToCurrentOrganization}/members-invitations`}
                matchExactPath={false}
                LeftIcon={Icons.Send}
                iconProps={genericIconProps}
              />
            ) : null}
            {canViewOptions ? (
              <SidePanelLink
                label={t('models.integration.model.name')}
                to={`${pathToCurrentOrganization}/options`}
                matchExactPath={false}
                LeftIcon={Icons.AddOption}
                description={t('models.integration.misc.integrations_explained')}
              />
            ) : null}
            <SidePanelLink
              label={t('titles.general_infos')}
              to={`${pathToCurrentOrganization}/info`}
              matchExactPath
              LeftIcon={Icons.InfoCircle}
              iconProps={genericIconProps}
            />
          </PanelLinkSection>

          {/* NETWORK */}
          {isNetwork && canViewNetworkAdmin ? (
            <PanelLinkSection title={t('titles.network_admin')}>
              {canEditNetwork ? (
                <>
                  <SidePanelLinkGroup
                    mainLink={{
                      id: 'network/members',
                      to: `${pathToCurrentOrganization}/network/members`,
                      label: t('titles.network_members'),
                      matchExactPath: false,
                      LeftIcon: Icons.GroupPersons
                    }}
                    subLinks={[
                      {
                        id: 'network/members/trials',
                        to: `${pathToCurrentOrganization}/network/members-trials`,
                        label: t('titles.on_trial'),
                        matchExactPath: false
                      },
                      {
                        id: 'network/invitations',
                        to: `${pathToCurrentOrganization}/network/invitations`,
                        label: t('models.invitation.model.name'),
                        matchExactPath: false
                      }
                    ]}
                  />
                  <SidePanelLink
                    label={t('titles.network_received_membership_requests')}
                    to={`${pathToCurrentOrganization}/network/membership-requests`}
                    matchExactPath={false}
                    LeftIcon={Icons.WaveHand}
                    indicator={!isNil(membershipTrialRequests) ? membershipTrialRequests.count : 0}
                  />
                </>
              ) : null}

              {canViewNetworkOptionRequests ? (
                <SidePanelLink
                  label={t('titles.network_received_option_activation_approval_requests')}
                  to={`${pathToCurrentOrganization}/network/option-requests`}
                  description={t('ecommerce.billing.subtitle')}
                  matchExactPath={false}
                  LeftIcon={Icons.RequestQuote}
                  indicator={!isNil(optionsRequest) ? optionsRequest.count : 0}
                />
              ) : null}
            </PanelLinkSection>
          ) : null}

          {/* BILLING */}
          {canViewBilling ? (
            <PanelLinkSection title={t('titles.billing')}>
              <SidePanelLink
                label={t('breadcrumbs./farms/*/admin/billing')}
                to={`${pathToCurrentOrganization}/billing`}
                description={t('ecommerce.billing.subtitle')}
                matchExactPath={false}
                LeftIcon={Icons.Wallet}
              />
            </PanelLinkSection>
          ) : null}
        </div>
      </ScrollablePanelSection>

      {/* FOOTER */}
      <PanelSection $gap='none'>
        {isPartOfANetwork ? null : (
          <SidePanelLink
            LeftIcon={Icons.Map}
            label={t('models.network.model.name', { capitalize: true })}
            to='/networks'
          />
        )}
        <SidePanelLink LeftIcon={Icons.Map} label={t('titles.devices_map')} to='/weather-map' />
        <SidePanelLink
          LeftIcon={Icons.Campaign}
          label={t('models.alert.model.name', { capitalize: true })}
          to='/alerts'
        />
        <Flex $flexDirection='row' $gap={'md'} $alignItems='center'>
          <Box $flex={1}>
            <SidePanelLink LeftIcon={Icons.Help} label={t('titles.help')} to='/help' />
          </Box>
          <TextTooltip content={t('actions.discover', { capitalize: true })}>
            <Link to='/discover'>
              <Icons.TravelExplore $size='lg' $p={'md'} onPress={noop} />
            </Link>
          </TextTooltip>
        </Flex>
      </PanelSection>
    </Panel>
  )
}

export default NavPanel
